/** Org default from pricing notification settings: a gap of this share of the shelf price is an alert. */
export const DEFAULT_SHELF_ALERT_PCT = 0.05;

export type ShelfVarianceTone = "up" | "down" | "flat" | "missing";

export type ShelfVarianceAttention = "alert" | "warning" | "quiet";

export type ShelfVariance = {
  delta_usd: number;
  /** Change as a percent of the shelf price. Null when the shelf price is zero. */
  percent: number | null;
  label: string;
  title: string;
  tone: ShelfVarianceTone;
};

function round2(amount: number): number {
  return Math.round(amount * 100) / 100;
}

function usd(amount: number): string {
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function signedUsd(amount: number): string {
  const body = usd(Math.abs(amount));
  if (amount > 0) return `+${body}`;
  if (amount < 0) return `−${body}`;
  return body;
}

/** Calculated sum compared with the price currently on the shelf. */
export function shelfVariance(calculated: number, shelf: number | null | undefined): ShelfVariance {
  if (shelf == null || !Number.isFinite(shelf)) {
    return {
      delta_usd: 0,
      percent: null,
      label: "No shelf price",
      title: "This product has no shelf price yet.",
      tone: "missing",
    };
  }
  const delta = round2(calculated - shelf);
  const percent = shelf > 0 ? round2((delta / shelf) * 100) : null;
  const tone: ShelfVarianceTone = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  if (tone === "flat") {
    return {
      delta_usd: 0,
      percent: 0,
      label: "Matches shelf",
      title: `Shelf price ${usd(shelf)} matches this sum.`,
      tone,
    };
  }
  const pct = percent == null ? "" : ` · ${delta > 0 ? "+" : "−"}${Math.abs(percent).toFixed(1)}%`;
  const direction = delta > 0 ? "above" : "below";
  return {
    delta_usd: delta,
    percent,
    label: `${signedUsd(delta)}${pct}`,
    title: `Shelf ${usd(shelf)}. This sum is ${usd(Math.abs(delta))} ${direction} that price.`,
    tone,
  };
}

export function shelfCostIsIncomplete(row: {
  materials_cost_usd: number | null;
  materials_unpriced_leaf_count?: number;
  materials_error_text?: string;
}): boolean {
  if (row.materials_cost_usd == null) return true;
  if ((row.materials_unpriced_leaf_count ?? 0) > 0) return true;
  return Boolean(row.materials_error_text);
}

function crossesShelfAlert(variance: ShelfVariance, thresholdPct: number): boolean {
  if (variance.tone !== "up" && variance.tone !== "down") return false;
  if (variance.percent == null) return Math.abs(variance.delta_usd) > 0.009;
  return Math.abs(variance.percent) + 1e-9 >= thresholdPct * 100;
}

/** A finished cost past the org threshold is an alert. The same gap on incomplete materials is a warning. */
export function shelfVarianceAttention(
  variance: ShelfVariance,
  incomplete: boolean,
  thresholdPct = DEFAULT_SHELF_ALERT_PCT,
): ShelfVarianceAttention {
  if (!crossesShelfAlert(variance, thresholdPct)) return "quiet";
  return incomplete ? "warning" : "alert";
}

export function shelfAlertPercentLabel(thresholdPct: number): string {
  const points = Math.round(thresholdPct * 1000) / 10;
  return `${Number.isInteger(points) ? points.toFixed(0) : points}%`;
}

export function shelfVarianceAttentionTitle(
  attention: ShelfVarianceAttention,
  variance: ShelfVariance,
  thresholdPct = DEFAULT_SHELF_ALERT_PCT,
): string {
  if (attention === "alert") {
    return `${variance.title} Alert: the gap is at least ${shelfAlertPercentLabel(thresholdPct)} of the shelf price.`;
  }
  if (attention === "warning") {
    return `${variance.title} Warning: materials are incomplete, so this is not a price alert.`;
  }
  return variance.title;
}
